/**
 * Matsedel (Meal Plan) Store
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiGet, apiPost, apiDelete } from '@/services/api-client';
import { useAuthStore } from './auth-store';

export interface MealPlanItem {
  id: string;
  familyId: string;
  type: 'recipe' | 'marker';
  // For recipes
  recipeId?: string;
  recipeName?: string;
  // For markers
  markerLabel?: string;
  markerEndDate?: string; // ISO date string
  position: number;
  createdAt: string;
  updatedAt: string;
}

interface MealPlanState {
  items: MealPlanItem[];
  isSyncing: boolean;
  fetchFromServer: (familyId?: string) => Promise<void>;
  addRecipe: (recipeId: string, recipeName: string) => Promise<void>;
  addMarker: (label: string, endDate?: string) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
  reorderItems: (items: MealPlanItem[]) => Promise<void>;
}

export const useMealPlanStore = create<MealPlanState>()(
  persist(
    (set, get) => ({
      items: [],
      isSyncing: false,

      fetchFromServer: async (familyId?: string) => {
        set({ isSyncing: true });
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const ownerId = getEffectiveFamilyOwnerId();
        const resolved = familyId || ownerId || useAuthStore.getState().user?.familyId;

        try {
          const url = resolved ? `/meal-plan?familyId=${resolved}` : '/meal-plan';
          const res = await apiGet<MealPlanItem[]>(url);
          if (res.success && res.data) {
            set({ items: res.data, isSyncing: false });
          } else {
            set({ items: [], isSyncing: false });
          }
        } catch (error) {
          console.error('Failed to fetch meal plan:', error);
          set({ items: [], isSyncing: false });
        }
      },

      addRecipe: async (recipeId: string, recipeName: string) => {
        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          
          const url = familyId ? `/meal-plan?familyId=${familyId}` : '/meal-plan';
          const res = await apiPost<MealPlanItem>(url, { type: 'recipe', recipeId, recipeName });
          if (res.success && res.data) {
            set((state) => ({ items: [...state.items, res.data!] }));
          }
        } catch (error) {
          console.error('Failed to add recipe to meal plan:', error);
          throw error;
        }
      },

      addMarker: async (label: string, endDate?: string) => {
        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          
          const url = familyId ? `/meal-plan?familyId=${familyId}` : '/meal-plan';
          const res = await apiPost<MealPlanItem>(url, { type: 'marker', markerLabel: label, markerEndDate: endDate });
          if (res.success && res.data) {
            set((state) => ({ items: [...state.items, res.data!] }));
          }
        } catch (error) {
          console.error('Failed to add marker to meal plan:', error);
          throw error;
        }
      },

      removeItem: async (id: string) => {
        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          
          const url = familyId ? `/meal-plan/${id}?familyId=${familyId}` : `/meal-plan/${id}`;
          await apiDelete(url);
          set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
        } catch (error) {
          console.error('Failed to remove meal plan item:', error);
          throw error;
        }
      },

      moveUp: async (id: string) => {
        const { items } = get();
        const index = items.findIndex((item) => item.id === id);
        if (index <= 0) return;

        const newItems = [...items];
        [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
        
        // Uppdatera positioner
        const updatedItems = newItems.map((item, i) => ({ ...item, position: i }));
        set({ items: updatedItems });

        // Synka med server
        await get().reorderItems(updatedItems);
      },

      moveDown: async (id: string) => {
        const { items } = get();
        const index = items.findIndex((item) => item.id === id);
        if (index < 0 || index >= items.length - 1) return;

        const newItems = [...items];
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        
        // Uppdatera positioner
        const updatedItems = newItems.map((item, i) => ({ ...item, position: i }));
        set({ items: updatedItems });

        // Synka med server
        await get().reorderItems(updatedItems);
      },

      reorderItems: async (items: MealPlanItem[]) => {
        // Optimistically update state first for immediate UI feedback
        set({ items });
        
        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          
          const reorderData = items.map((item, index) => ({ id: item.id, position: index }));
          const url = familyId ? `/meal-plan/reorder?familyId=${familyId}` : '/meal-plan/reorder';
          await apiPost(url, { items: reorderData });
        } catch (error) {
          console.error('Failed to reorder meal plan:', error);
          // If API call fails, refetch to restore correct state
          await get().fetchFromServer();
          throw error;
        }
      },
    }),
    {
      name: 'meal-plan-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);
